/**
 * ProfessionalDashboardPage Component
 * Dashboard for salon staff (professionals, receptionists, managers)
 * Features: Today's appointments, quick actions, professional tools
 */

import React, { useState, useEffect } from 'react';
import ProfessionalBottomNavigation from '../components/ProfessionalBottomNavigation';
import { useAuthStore } from '../stores/authStore';
import dashboardService from '../services/dashboardService';
import { 
  Calendar, 
  Clock, 
  Users, 
  Bell,
  CheckSquare,
  DollarSign,
  User
} from 'lucide-react';

const ProfessionalDashboardPage = () => {
  const { user } = useAuthStore();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch dashboard data on component mount
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await dashboardService.getTodayStats();
        setDashboardData(data);
      } catch (err) {
        setError(err.message || 'Erro ao carregar dados do dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);



  // Render welcome header - Luxury Design
  const renderWelcomeHeader = () => (
    <div className="safe-area-top bg-brand-dark dark:bg-brand-light px-6 py-6 border-b border-brand-light/10 dark:border-brand-dark/10">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-brand-light/20 dark:bg-brand-dark/20 border border-brand-light/20 dark:border-brand-dark/20 flex items-center justify-center">
            <User size={24} className="text-brand-light dark:text-brand-dark" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="text-xl font-serif font-bold text-brand-light dark:text-brand-dark tracking-wide">
              Olá, {user?.fullName?.split(' ')[0] || 'Colaborador'}!
            </h1>
          </div>
        </div>
        <button className="p-2 bg-transparent border border-brand-light/20 dark:border-brand-dark/20 hover:bg-brand-light/10 dark:hover:bg-brand-dark/10 transition-all duration-300">
          <Bell size={20} className="text-brand-light dark:text-brand-dark" strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );

  // Render quick stats - Luxury Design
  const renderQuickStats = () => {

    return (
      <div className="px-6 py-6">
        <h2 className="text-lg font-serif font-bold text-brand-dark dark:text-brand-light mb-4 tracking-wide">Resumo de Hoje</h2>
        <div className="grid grid-cols-2 gap-4">
        <div className="bg-brand-light dark:bg-brand-dark border border-brand-dark/20 dark:border-brand-light/20 p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-transparent border border-brand-dark/20 dark:border-brand-light/20 flex items-center justify-center">
              <Calendar size={20} className="text-brand-dark dark:text-brand-light" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-xs text-brand-dark/60 dark:text-brand-light/60 uppercase tracking-wider">Agendamentos</p>
              <p className="text-xl font-serif font-bold text-brand-dark dark:text-brand-light tracking-wide">
                {loading ? '...' : dashboardData?.appointments || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-brand-light dark:bg-brand-dark border border-brand-dark/20 dark:border-brand-light/20 p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-transparent border border-green-600/20 dark:border-green-400/20 flex items-center justify-center">
              <CheckSquare size={20} className="text-green-600 dark:text-green-400" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-xs text-brand-dark/60 dark:text-brand-light/60 uppercase tracking-wider">Concluídos</p>
              <p className="text-xl font-serif font-bold text-brand-dark dark:text-brand-light tracking-wide">
                {loading ? '...' : dashboardData?.completed || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-brand-light dark:bg-brand-dark border border-brand-dark/20 dark:border-brand-light/20 p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-transparent border border-brand-dark/20 dark:border-brand-light/20 flex items-center justify-center">
              <Users size={20} className="text-brand-dark dark:text-brand-light" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-xs text-brand-dark/60 dark:text-brand-light/60 uppercase tracking-wider">Clientes</p>
              <p className="text-xl font-serif font-bold text-brand-dark dark:text-brand-light tracking-wide">
                {loading ? '...' : dashboardData?.clients || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-brand-light dark:bg-brand-dark border border-brand-dark/20 dark:border-brand-light/20 p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-transparent border border-brand-dark/20 dark:border-brand-light/20 flex items-center justify-center">
              <DollarSign size={20} className="text-brand-dark dark:text-brand-light" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-xs text-brand-dark/60 dark:text-brand-light/60 uppercase tracking-wider">Faturamento</p>
              <p className="text-xl font-serif font-bold text-brand-dark dark:text-brand-light tracking-wide">
                {loading ? '...' : dashboardData?.revenue || 'R$ 0,00'}
              </p>
            </div>
          </div>
        </div>
        </div>
      </div>
    );
  };

  // Render next appointments - Luxury Design
  const renderNextAppointments = () => (
    <div className="px-6 pb-6">
      <div className="mb-4">
        <h2 className="text-lg font-serif font-bold text-brand-dark dark:text-brand-light tracking-wide">Próximos Agendamentos</h2>
      </div>

      <div className="space-y-3">
        {loading ? (
          <div className="bg-brand-light dark:bg-brand-dark border border-brand-dark/20 dark:border-brand-light/20 p-4">
            <p className="text-brand-dark/60 dark:text-brand-light/60 text-center text-xs uppercase tracking-wider">Carregando agendamentos...</p>
          </div>
        ) : error ? (
          <div className="bg-brand-light dark:bg-brand-dark border border-red-600/30 dark:border-red-400/30 p-4">
            <p className="text-red-600 dark:text-red-400 text-center tracking-wide">Erro ao carregar agendamentos</p>
          </div>
        ) : dashboardData?.nextAppointments && dashboardData.nextAppointments.length > 0 ? (
          dashboardData.nextAppointments.map((appointment, index) => (
            <div key={appointment.id || index} className="bg-brand-light dark:bg-brand-dark border border-brand-dark/20 dark:border-brand-light/20 p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-medium text-brand-dark dark:text-brand-light tracking-wide">{appointment.service}</h3>
                <span className={`text-xs px-2 py-1 border uppercase tracking-wider ${
                  appointment.status === 'Confirmado' ? 'bg-transparent border-blue-600/30 dark:border-blue-400/30 text-blue-600 dark:text-blue-400' :
                  appointment.status === 'Em Andamento' ? 'bg-transparent border-amber-600/30 dark:border-amber-400/30 text-amber-600 dark:text-amber-400' :
                  appointment.status === 'Agendado' ? 'bg-transparent border-green-600/30 dark:border-green-400/30 text-green-600 dark:text-green-400' :
                  appointment.status === 'Encaixe' ? 'bg-transparent border-brand-dark/30 dark:border-brand-light/30 text-brand-dark dark:text-brand-light' :
                  appointment.status === 'Concluído' ? 'bg-transparent border-green-600/30 dark:border-green-400/30 text-green-600 dark:text-green-400' :
                  appointment.status === 'Cancelado' ? 'bg-transparent border-red-600/30 dark:border-red-400/30 text-red-600 dark:text-red-400' :
                  'bg-transparent border-brand-dark/30 dark:border-brand-light/30 text-brand-dark dark:text-brand-light'
                }`}>
                  {appointment.status}
                </span>
              </div>
              <div className="flex items-center space-x-4 text-sm text-brand-dark/60 dark:text-brand-light/60">
                <div className="flex items-center space-x-1">
                  <Clock size={14} strokeWidth={1.5} />
                  <span className="tracking-wide">{appointment.time}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <User size={14} strokeWidth={1.5} />
                  <span className="tracking-wide">{appointment.client}</span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-brand-light dark:bg-brand-dark border border-brand-dark/20 dark:border-brand-light/20 p-4">
            <p className="text-brand-dark/60 dark:text-brand-light/60 text-center tracking-wide">Nenhum agendamento próximo</p>
          </div>
        )}
      </div>
    </div>
  );


  return (
    <div className="h-full flex flex-col bg-brand-light dark:bg-brand-dark">
      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-y-auto mobile-scroll">
          {renderWelcomeHeader()}

          {/* Main Content */}
          <div className="flex-1 bg-brand-light dark:bg-brand-dark min-h-0">
            {error && (
              <div className="px-6 py-4">
                <div className="bg-transparent border border-red-600/30 dark:border-red-400/30 p-4 mb-4">
                  <p className="text-red-600 dark:text-red-400 text-sm tracking-wide">
                    ⚠️ {error}
                  </p>
                  <button
                    onClick={() => window.location.reload()}
                    className="mt-2 text-red-600 dark:text-red-400 text-sm font-medium uppercase tracking-wider hover:text-red-700 dark:hover:text-red-500"
                  >
                    Tentar novamente
                  </button>
                </div>
              </div>
            )}
            {renderQuickStats()}
            {renderNextAppointments()}
          </div>
        </div>
      </div>
      <ProfessionalBottomNavigation />
    </div>
  );
};

export default ProfessionalDashboardPage;